import { Provider } from '@/components/ui/provider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { PropsWithChildren, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { BrowserRouter } from 'react-router';
import { AnalyticsProvider } from './analytics-provider';
import { AuthProvider } from './auth-provider';
import { HomeBannerProvider } from './home-banner-provider';
import { ReownAppKitProvider } from './reown-appkit-provider';

// If no trackingId is provided, analytics will not be initialized
const GA_TRACKING_ID = import.meta.env.VITE_GA_TRACKING_ID || '';

export const Providers = ({
  children,
  queryClientOverride,
}: { queryClientOverride?: QueryClient } & PropsWithChildren) => {
  // Need to memoize queryClient otherwise all providers will re-render every cycle
  const queryClient = useMemo(() => queryClientOverride ?? new QueryClient(), [queryClientOverride]);

  return (
    <ErrorBoundary
      fallback={<p>⚠️ Something went wrong, please try again later</p>}
      onError={(error, errorInfo) => {
        console.error('Error:', error, errorInfo);
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ReownAppKitProvider queryClient={queryClient}>
          <BrowserRouter>
            <AuthProvider>
              <Provider>
                <HomeBannerProvider>
                  <AnalyticsProvider trackingId={GA_TRACKING_ID}>{children}</AnalyticsProvider>
                </HomeBannerProvider>
              </Provider>
              {/* ReactQueryDevtools is automatically excluded from production builds so does not need to be conditionally rendered */}
              <ReactQueryDevtools initialIsOpen={false} />
            </AuthProvider>
          </BrowserRouter>
        </ReownAppKitProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};
